import './pwa-banner.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import Close from '@/assets/icons/close-normal.svg';
import CloseIcon from '@/assets/icons/close.svg';

import {
  clearUrlParams,
  getLocalStorageItem,
  getUserAgent,
  isClient,
  PWA_GUIDE_CLOSED_EVENT,
  setLocalStorageItem,
} from '../../helpers/utils';
import Button from '../ui/button/button';
import { SideModal } from '../ui/side-modal/side-modal';
import { SideModalBody } from '../ui/side-modal/side-modal-body';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { isBrowser } from '@/helpers/is-browser';
import { SafariGuide } from './safari-guide';
import Backdrop from '../ui/backdrop/backdrop';
import { ILibraryMeetingPlaceDictionary } from '@/lib/fragments/libraryMeetingPlaceDictionary';

interface PWABannerProps {
  dictionary?: ILibraryMeetingPlaceDictionary;
  onClose?: () => void;
  onInstall?: () => void;
  node_locale: string;
  meetingPlaceName: string;
}

type DeviceType = 'ios' | 'android' | 'desktop' | '';

const PWABanner: React.FC<PWABannerProps> = ({
  dictionary,
  onClose,
  onInstall,
  node_locale,
  meetingPlaceName,
}) => {
  const [type, setType] = useState<DeviceType>('');
  const [isStandalone, setIsStandalone] = useState<boolean>(false);
  const [showIOSInstructions, setShowIOSInstructions] =
    useState<boolean>(false);
  const [canInstall, setCanInstall] = useState<boolean>(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [isPWAMobileViewOpen, setIsPWAMobileViewOpen] =
    useState<boolean>(false);
  const [isSafari, setIsSafari] = useState<boolean>(false);
  const [isGuideClosed, setIsGuideClosed] = useState<boolean>(false);

  // Use ref to store the deferred prompt to avoid closure issues
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);

  const sendGtmEvent = useCallback(
    (action: 'Closed' | 'Installed' | 'Details viewed') => {
      if (isBrowser && dataLayer) {
        dataLayer.push({
          event: 'pwa-banner',
          action,
          meeting_place: meetingPlaceName,
        });
      }
    },
    [meetingPlaceName],
  );

  // Detect device type and if already running as PWA
  useEffect(() => {
    if (!isClient) return;

    // Check if already running as standalone PWA.
    // iOS Safari does not fire beforeinstallprompt/appinstalled, so detecting
    // standalone mode is the only signal that the PWA has been installed.
    const isInStandaloneMode =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    setIsStandalone(isInStandaloneMode);

    // Persist the installed flag so the banner stays hidden even when the site
    // is later reopened in a normal browser tab (where standalone is false).
    if (isInStandaloneMode) {
      setLocalStorageItem('pwaBanner-installed', 'true');
    }

    const ua = getUserAgent();
    if (ua) {
      const osName = ua.os?.name;

      const browserName = ua.browser?.name;

      const isSafariBrowser =
        browserName === 'Safari' || browserName === 'MobileSafari';
      setIsSafari(isSafariBrowser);

      if (osName === 'iOS') {
        setType('ios');
      } else if (osName === 'Android') {
        setType('android');
      } else {
        setType('desktop');
      }
    }
  }, []);

  const hide = useCallback(() => {
    if (isClient) {
      window.document.querySelector('html')?.classList.remove('pwaBanner-show');
    }
  }, []);

  const show = useCallback(() => {
    if (isClient) {
      window.document.querySelector('html')?.classList.add('pwaBanner-show');
    }
  }, []);

  const handleClose = useCallback(() => {
    hide();
    setShowIOSInstructions(false);
    setLocalStorageItem('pwaBanner-closed', 'true');
    sendGtmEvent('Closed');

    if (onClose) {
      onClose();
    }
  }, [hide, onClose, sendGtmEvent]);

  // Closing the Safari install guide (sidebar or bottom sheet) dismisses the
  // PWA prompt for good: persist a flag and broadcast an event so both the
  // banner and the homepage tile hide immediately, regardless of which surface
  // opened the guide, and stay hidden on subsequent visits.
  const handleGuideClose = useCallback(() => {
    setLocalStorageItem('pwaGuideClosed', 'true');
    setIsGuideClosed(true);
    setIsSidebarOpen(false);
    setIsPWAMobileViewOpen(false);
    setShowIOSInstructions(false);
    hide();
    if (isBrowser) {
      clearUrlParams();
      window.dispatchEvent(new Event(PWA_GUIDE_CLOSED_EVENT));
    }
  }, [hide]);

  // Sync the guide-closed state across components (banner <-> tile) without a
  // page refresh: read the persisted flag on mount and listen for the event.
  useEffect(() => {
    if (!isClient) return;

    if (getLocalStorageItem('pwaGuideClosed') === 'true') {
      setIsGuideClosed(true);
    }

    const handleGuideClosed = () => setIsGuideClosed(true);
    window.addEventListener(PWA_GUIDE_CLOSED_EVENT, handleGuideClosed);

    return () => {
      window.removeEventListener(PWA_GUIDE_CLOSED_EVENT, handleGuideClosed);
    };
  }, []);

  // Handle install button click
  const handleInstall = useCallback(async () => {
    const deferredPrompt = deferredPromptRef.current;

    // For browsers that support beforeinstallprompt (Chrome, Edge, Opera on Android/Desktop)
    if (deferredPrompt) {
      try {
        // Show the install prompt
        await deferredPrompt.prompt();

        // Wait for the user's response
        const choice = await deferredPrompt.userChoice;

        if (choice.outcome === 'accepted') {
          setLocalStorageItem('pwaBanner-installed', 'true');
          hide();
        }

        // Clear the deferred prompt - it can only be used once
        deferredPromptRef.current = null;
        setCanInstall(false);

        if (onInstall) {
          onInstall();
        }
      } catch (error) {
        console.error('Error showing install prompt:', error);
      }
    } else if (type === 'ios' || (type == 'desktop' && isSafari)) {
      // For iOS Safari, show instructions since beforeinstallprompt is not supported
      if (isMobile) {
        setIsPWAMobileViewOpen(true);
      } else {
        setIsSidebarOpen(true);
      }
      setShowIOSInstructions(true);
      sendGtmEvent('Details viewed');
    }
  }, [type, isSafari, hide, onInstall, sendGtmEvent]);

  useEffect(() => {
    isPWAMobileViewOpen
      ? document.body.classList.add('backdrop')
      : document.body.classList.remove('backdrop');
  }, [isPWAMobileViewOpen]);

  // Listen for the beforeinstallprompt event
  useEffect(() => {
    if (!isClient) return;

    const handler = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event in ref for later use (avoids closure issues)
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      setCanInstall(true);
      // Only show banner if not already closed or installed
      const bannerClosed = getLocalStorageItem('pwaBanner-closed');
      const bannerInstalled = getLocalStorageItem('pwaBanner-installed');
      if (!bannerClosed && !bannerInstalled) {
        show();
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Also listen for successful installation
    const handleAppInstalled = () => {
      setLocalStorageItem('pwaBanner-installed', 'true');
      sendGtmEvent('Installed');
      deferredPromptRef.current = null;
      setCanInstall(false);
      hide();
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [show, hide, sendGtmEvent]);

  // Determine if banner should be visible
  const isClosed = getLocalStorageItem('pwaBanner-closed');
  const isInstalled = getLocalStorageItem('pwaBanner-installed');
  const shouldHide =
    isStandalone || isClosed || isInstalled || isGuideClosed || !type;

  useEffect(() => {
    if (
      (type === 'ios' || (type === 'desktop' && isSafari)) &&
      !isStandalone &&
      !isClosed &&
      !isInstalled &&
      !isGuideClosed
    ) {
      show();
    }
  }, [
    type,
    isStandalone,
    isSafari,
    isClosed,
    isInstalled,
    isGuideClosed,
    show,
  ]);

  const isMobile = useIsMobile();

  // // Effect to handle hiding the banner and cleanup
  useEffect(() => {
    if (shouldHide) {
      hide();
    }

    // Cleanup: remove the class when component unmounts
    return () => {
      hide();
    };
  }, [shouldHide, hide]);

  // Don't render if should be hidden
  if (shouldHide) {
    return null;
  }

  return (
    <>
      <div className="pwaBanner bg-background-primary-light py-2 px-4">
        <div className="flex h-15 items-center">
          <button type="button" aria-label="close" onClick={handleClose}>
            <Close
              className="h-2 w-2 cursor-pointer from-neutral-grey-400"
              viewBox="0 0 14 14"
            />
          </button>
          <div className="flex-col pl-3">
            <div className="text-bodys sm:text-bodyl text-neutral-grey-400">
              {/* {dictionary?.pwaTopBanner} */}
              {dictionary?.pwaTopBanner?.replace(
                '{Meeting Place}',
                meetingPlaceName,
              )}
            </div>
          </div>
          <div className="ml-auto">
            <Button
              variant="primary"
              className="text-center font-buttonlabelm text-neutral-grey-700"
              onClick={handleInstall}
            >
              {dictionary?.install}
            </Button>
          </div>
        </div>
      </div>
      {!isMobile && isSidebarOpen && showIOSInstructions && (
        <SideModal isOpen={isSidebarOpen} handleClose={handleGuideClose}>
          <SideModalBody>
            <>
              <SafariGuide
                node_locale={node_locale}
                meetingPlaceName={meetingPlaceName}
              />
            </>
          </SideModalBody>
        </SideModal>
      )}
      {isPWAMobileViewOpen && showIOSInstructions && isMobile && (
        <>
          <Backdrop
            handleOpen={handleGuideClose}
            showButtons={false}
            className="!min-h-[250px]"
          >
            <>
              <SafariGuide
                node_locale={node_locale}
                isMobile={true}
                handleClose={handleGuideClose}
                meetingPlaceName={meetingPlaceName}
              />
              <button
                className="absolute -top-20 right-4 z-[200] grid h-12 w-12 place-items-center rounded-full bg-brand-grey-1 outline-0 transition-all focus:bg-brand-grey-2 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-brand-grey-2 active:bg-brand-grey-2 lg:hidden lg:h-15 lg:w-15 lg:bg-brand-yellow-1 lg:hover:bg-accent-primary-dark lg:focus:bg-accent-primary-dark lg:active:bg-accent-primary-dark"
                onClick={handleGuideClose}
              >
                <CloseIcon />
              </button>
            </>
          </Backdrop>
        </>
      )}
    </>
  );
};

export default PWABanner;
